import { FeedbackConfig, LocalizedString, LocalizedStringArray } from "@/features/Feedback/api/feedbackApi";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import { Input } from "@/shared/components/ui/input";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";

export interface FeedbackModalProps {
  open: boolean;
  config: FeedbackConfig | null;
  loading?: boolean;
  onClose: (reason?: "submitted" | "dismissed") => void;
  onSubmit: (answers: Record<string, unknown>) => Promise<boolean>;
}

export function FeedbackModal({ open, config, loading, onClose, onSubmit }: FeedbackModalProps) {
  const { t, i18n } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  const title = useMemo(() => {
    if (!config?.title) return "";
    const lang = i18n.language?.startsWith("pt") ? "pt" : "en";
    return config.title[lang] || config.title.en || config.title.pt || "";
  }, [config, i18n.language]);

  const resolveText = (value?: LocalizedString): string => {
    if (!value) return "";
    if (typeof value === "string") return value;
    const lang = i18n.language?.startsWith("pt") ? "pt" : "en";
    return value[lang] || value.en || value.pt || "";
  };

  const resolveOptions = (value?: LocalizedStringArray): string[] => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    const lang = i18n.language?.startsWith("pt") ? "pt" : "en";
    return value[lang] || value.en || value.pt || [];
  };

  if (!config) {
    return null;
  }

  const handleChange = (key: string, value: unknown) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const ok = await onSubmit(formValues);
      if (ok) {
        onClose("submitted");
      } else {
        setSubmitting(false);
      }
    } catch {
      setSubmitting(false);
    }
  };

  const handleDismiss = () => {
    onClose("dismissed");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { handleDismiss(); } }}>
      <DialogContent className="max-w-lg w-full">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {config.form_fields.map(field => {
            const label = resolveText(field.label);
            const description = resolveText(field.description);
            const placeholder = resolveText(field.placeholder);
            const options = resolveOptions(field.options);

            return (
            <div key={field.key} className="space-y-1">
              <Label htmlFor={field.key}>
                {label}
                {field.required && <span className="text-destructive ml-1">*</span>}
              </Label>
              {description && (
                <p className="text-xs text-muted-foreground">{description}</p>
              )}

              {field.type === "textarea" && (
                <Textarea
                  id={field.key}
                  placeholder={placeholder}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              )}

              {field.type === "select" && options.length > 0 && (
                <Select onValueChange={(value) => handleChange(field.key, value)}>
                  <SelectTrigger id={field.key}>
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map(option => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {field.type === "rating" && (
                <div className="flex items-center gap-1">
                  {Array.from({ length: 5 }).map((_, index) => {
                    const value = index + 1;
                    const current = (formValues[field.key] as number | undefined) ?? 0;
                    const filled = value <= current;

                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => handleChange(field.key, value)}
                        className="p-0.5"
                      >
                        <Star
                          className={
                            filled
                              ? "w-5 h-5 text-yellow-400 fill-yellow-400"
                              : "w-5 h-5 text-muted-foreground"
                          }
                        />
                      </button>
                    );
                  })}
                </div>
              )}

              {field.type === "text" && (
                <Input
                  id={field.key}
                  placeholder={placeholder}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                />
              )}
            </div>
          )})}
        </div>

        <DialogFooter className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={handleDismiss}
            disabled={submitting || loading}
          >
            {t("common.close", { defaultValue: "Close" })}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || loading}>
            {t("common.actions.submit", { defaultValue: "Submit" })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
